/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import type { User } from '../types/User'
import type { LoginCredentials, AuthState } from '../types/Auth'
import { clearAuthStorage, resetLoginUiStorage } from './authStorage';
import authService from '../services/auth.service';

// Define Auth Context Type
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  setCurrentUser: (user: User) => void;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const hasValidSessionUser = (user: User | null | undefined): user is User => {
  return Boolean(user?._id) && typeof user?.role === 'string' && user.role.length > 0;
};

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isImpersonating: false,
  isLoading: true,
  error: null,
};

// Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Reducer
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      if (!hasValidSessionUser(action.payload.user)) {
        return {
          ...state,
          user: null,
          isImpersonating: false,
          isAuthenticated: false,
          isLoading: false,
          error: 'Invalid session user payload.',
        };
      }
      return {
        ...state,
        user: action.payload.user,
        isImpersonating: !!action.payload.user?.impersonated,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isImpersonating: false,
        isLoading: false,
        isAuthenticated: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        user: null,
        isAuthenticated: false,
        isImpersonating: false,
        isLoading: false,
        error: null,
      };
    default:
      return state;
  }
}

// Auth Provider
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const authRequestId = useRef(0);
  const nextAuthRequestId = () => {
    authRequestId.current += 1;
    return authRequestId.current;
  };

  // Check authentication status
  const checkAuthStatus = useCallback(async () => {
    authRequestId.current += 1;
    const requestId = authRequestId.current;
    dispatch({ type: 'AUTH_START' });
    try {
      const user = await authService.getCurrentUser();
      if (requestId !== authRequestId.current) return;
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user },
      });
    } catch {
      if (requestId !== authRequestId.current) return;
      clearAuthStorage();
      dispatch({
        type: 'AUTH_ERROR',
        payload: 'Session expired. Please login again.',
      });
    }
  }, []);

  useEffect(() => {
    void checkAuthStatus();
  }, [checkAuthStatus]);

  // Login
  const login = async (credentials: LoginCredentials) => {
    const requestId = nextAuthRequestId();
    dispatch({ type: 'AUTH_START' });
    try {
      await authService.login(credentials);
      const authenticatedUser = await authService.getCurrentUser();
      if (requestId !== authRequestId.current) return;
      resetLoginUiStorage();
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: authenticatedUser },
      });
    } catch (error) {
      if (requestId !== authRequestId.current) return;
      clearAuthStorage();
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Login failed',
      });
      throw error;
    }
  };

  // Logout
  const logout = () => {
    nextAuthRequestId();
    authService.logout();
    dispatch({ type: 'AUTH_LOGOUT' });
  };

  const setCurrentUser = (user: User) => {
    nextAuthRequestId();
    if (!hasValidSessionUser(user)) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: 'Invalid session user payload.',
      });
      return;
    }
    dispatch({
      type: 'AUTH_SUCCESS',
      payload: { user },
    });
  };

  const startImpersonation = async (userId: string) => {
    const requestId = nextAuthRequestId();
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.startImpersonation(userId);
      if (requestId !== authRequestId.current) return;
      let authenticatedUser = response.user as User;
      try {
        authenticatedUser = await authService.getCurrentUser();
      } catch {
        // Fall back to the impersonation response if the follow-up user fetch is temporarily unavailable.
      }
      if (requestId !== authRequestId.current) return;
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authenticatedUser,
        },
      });
    } catch (error) {
      if (requestId !== authRequestId.current) return;
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Impersonation failed',
      });
      throw error;
    }
  };

  const stopImpersonation = async () => {
    const requestId = nextAuthRequestId();
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.stopImpersonation();
      if (requestId !== authRequestId.current) return;
      let authenticatedUser = response.user as User;
      try {
        authenticatedUser = await authService.getCurrentUser();
      } catch {
        // Fall back to the stop impersonation response if the follow-up user fetch is temporarily unavailable.
      }
      if (requestId !== authRequestId.current) return;
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authenticatedUser,
        },
      });
    } catch (error) {
      if (requestId !== authRequestId.current) return;
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Stop impersonation failed',
      });
      throw error;
    }
  };

  // Value object
  const value = {
    ...state,
    login,
    logout,
    checkAuthStatus,
    setCurrentUser,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook for using Auth Context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
